import { none, useHookstate } from "@hookstate/core";
import InputField from "../input-field/InputField"
import { accessGlobalMiscState, useGlobalMiscState } from "../../stores/misc";
import { Demerit, DisciplinaryCase } from "../../types";
import { useEffect } from "react";
import { onInputChangeNative, showSnackbar } from "../../misc/utils";
import { useUniversalFind } from "../../hooks/universal";
import DisciplinaryTableItem from "../disciplinary-table-item/DisciplinaryTableItem";
import DefaultButton from "../default-button/DefaultButton";
import Plus from "../../assets/icons/Plus";
import Cross from "../../assets/icons/Cross";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Tick from "../../assets/icons/Tick";
import { simpleGetter } from "../../actions/common";
import { demeritsApi } from "../../api/api";

const categoryList = [
    {
        label: 'Тип нарушения',
        additionalClassName: 'w-[16%]'
    },
    {
        label: 'Студент',
        additionalClassName: 'w-[22%]'
    },
    {
        label: 'Дата создания',
        additionalClassName: 'w-[16%]'
    },
    {
        label: 'Статус',
        additionalClassName: 'w-[16%]'
    },
    {
        label: '',
        additionalClassName: 'w-[30%]'
    },
];

export const EditDisciplinaryCase = () => {
    const localState = useHookstate({});
    const state: Record<string, any> = localState.get();
    const name = state.name || "";
    const miscState = useGlobalMiscState();
    const disciplinaryCasesDemeritCurrentCase: DisciplinaryCase = miscState.get('disciplinaryCasesDemeritCurrentCase') || {}
    const notInCaseList: Demerit[] = state.notInCaseList || [];
    const inCaseList: Demerit[] = state.inCaseList || [];
    const demerits: Demerit[] = useUniversalFind('Demerit', {}, {
        include: ['main_user', 'demerit_type', 'media']
    })

    useEffect(() => {
        if (disciplinaryCasesDemeritCurrentCase.id) {
            localState.merge({
                name: disciplinaryCasesDemeritCurrentCase.name,
                notInCaseList: demerits.filter(d => !disciplinaryCasesDemeritCurrentCase.demerits?.map(dem => dem.id).includes(d.id)),
                inCaseList: disciplinaryCasesDemeritCurrentCase.demerits?.slice().map(i => ({ ...i }))
            })
        }
    }, [disciplinaryCasesDemeritCurrentCase.id, demerits.length, disciplinaryCasesDemeritCurrentCase.demerits?.length])

    const addToList = (from: string, to: string, item: Demerit) => () => {
        // @ts-ignore
        localState[to]?.merge([JSON.parse(JSON.stringify(item))])
        //@ts-ignore
        const foundIndex = localState[from]?.get()?.findIndex?.(i => i.id === item.id)
        if (foundIndex >= 0) {
            // @ts-ignore
            localState[from][foundIndex].set(none)
        }
    }

    const notInCaseButtonRender = (item: Demerit) => {
        return (
            <td className="py-2 pr-2">
                <DefaultButton
                    Icon={Plus}
                    buttonText="Добавить в дело"
                    additionalStyle="w-full py-[6px] px-2 text-xs leading-tight whitespace-normal border-valley-dark-green text-valley-dark-green"
                    onClick={addToList('notInCaseList', 'inCaseList', item)}
                />
            </td>
        )
    }

    const inCaseButtonRender = (item: Demerit) => {
        return (
            <td className="py-2 pr-2">
                <DefaultButton
                    Icon={Cross}
                    buttonText="Убрать из дела"
                    additionalStyle="w-full py-[6px] px-2 text-xs leading-tight whitespace-normal border-valley-red text-valley-red"
                    onClick={addToList('inCaseList', 'notInCaseList', item)}
                />
            </td>
        )
    }

    const submit = async () => {
        const body = {
            id: disciplinaryCasesDemeritCurrentCase.id,
            name,
            demerits: inCaseList
        }
        const response = await simpleGetter(demeritsApi.updateDisciplinaryCase, body)
        showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
        if (response.success) {
            miscState.setBatch({
                disciplinaryCasesEditCaseModalOpen: false,
                disciplinaryCasesTriggerFetch: (miscState.get('disciplinaryCasesTriggerFetch') || 0) + 1
            })
        }
    }

    const validateForm = () => name.length && inCaseList.length

    return (
        <div className="flex flex-col flex-1 items-center overflow-auto">
            <div className="w-full md:w-2/3 lg:w-1/2 flex flex-col justify-center items-center">
                <span className="text-xl md:text-2xl text-valley-black mb-4 md:mb-6 text-center">
                    Изменение дела
                </span>
                <InputField
                    type="text"
                    label="Название дела"
                    value={name}
                    handleChange={onInputChangeNative(localState, 'name')}
                    additionalClassName='bg-valley-background-blue rounded-lg'
                    wrapperClassName="w-full"
                />
            </div>
            <div className="w-full mt-6 md:mt-8 flex-1 flex flex-col 2xl:flex-row gap-3 overflow-auto min-h-0">
                <div className="flex flex-col flex-1 min-h-0 p-4 md:p-6 gap-4 md:gap-6 bg-valley-background-blue rounded-xl">
                    <span className="text-valley-gray text-lg md:text-2xl font-semibold leading-tight md:leading-[26.4px]">
                        Нарушения не в деле
                    </span>
                    <div className="flex flex-col flex-1 overflow-auto">
                        <table className='table-fixed w-full rounded-lg bg-white'>
                            <thead>
                                <tr>
                                    {categoryList.map((category, index) => (
                                        <th key={index} className={`px-2 md:px-4 py-2 border-b text-left text-xs md:text-sm font-medium text-valley-gray ${category.additionalClassName}`}>
                                            {category.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Boolean(notInCaseList.length) && notInCaseList.map((item, index) => (
                                    <DisciplinaryTableItem customButtonRender={notInCaseButtonRender} key={index} item={item} index={index} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="flex flex-col flex-1 min-h-0 p-4 md:p-6 gap-4 md:gap-6 bg-valley-background-blue rounded-xl">
                    <span className="text-valley-gray text-lg md:text-2xl font-semibold leading-tight md:leading-[26.4px]">
                        Нарушения не в деле
                    </span>
                    <div className="flex flex-col flex-1 overflow-auto">
                        <table className='table-fixed w-full rounded-lg bg-white'>
                            <thead>
                                <tr>
                                    {categoryList.map((category, index) => (
                                        <th key={index} className={`px-2 md:px-4 py-2 border-b text-left text-xs md:text-sm font-medium text-valley-gray ${category.additionalClassName}`}>
                                            {category.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Boolean(inCaseList.length) && inCaseList.map((item, index) => (
                                    <DisciplinaryTableItem customButtonRender={inCaseButtonRender} key={index} item={item} index={index} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="mt-6 md:mt-8 w-full md:w-3/5 flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3">
                <DefaultButton
                    Icon={ArrowLeft}
                    buttonText='Вернуться'
                    additionalStyle='flex-1 bg-valley-gray text-white py-3'
                    onClick={() => miscState.set('disciplinaryCasesEditCaseModalOpen', false)}
                />
                <DefaultButton
                    Icon={Tick}
                    buttonText='Сохранить изменения'
                    additionalStyle='flex-1 bg-valley-main text-white py-3'
                    disabled={!validateForm()}
                    onClick={submit}
                />
            </div>
        </div>
    )
}